import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import Cookies from 'react-cookies';
import { object, string, ref } from 'yup';
import COOKIES from '../../config/cookies/cookies';
import { TYPE_UNDERLINED } from '../../config/form/form';
import {
  URLACTIVATEPASSWORD,
  URLRESETPASSWORD,
} from '../../config/setup/setup';
import {
  RESET_PASSWORD_TITLE,
  RESET_PASSWORD_PLACEHOLDER_PASSWORD,
  RESET_PASSWORD_PLACEHOLDER_CONFIRM,
  REQUIRED_FIELD,
  RESET_PASSWORD_BTN,
  RESET_PASSWORD_ERROR_MIN,
  RESET_PASSWORD_ERROR,
} from '../../config/text/text';
import { resetPassword } from '../../helpers/userService';
import ButtonForm from '../_generic/button/CTAButton';
import WrappedInput from '../_generic/form/WrappedInput';
import Tile from '../_generic/tile/Tile';
import TileBody from '../_generic/tile/TileBody';
import useToast from '../_generic/toast/UseToast';

const schema = object().shape({
  password: string().required(REQUIRED_FIELD).min(6, RESET_PASSWORD_ERROR_MIN),
  passwordconfirm: string()
    .required(REQUIRED_FIELD)
    .oneOf([ref('password'), null], RESET_PASSWORD_PLACEHOLDER_CONFIRM),
});
const ResetPasswordForm = ({
  buttonText = RESET_PASSWORD_BTN,
  confirmPasswordPlaceholder = RESET_PASSWORD_PLACEHOLDER_CONFIRM,
  isActivatePW = false,
  passwordPlaceholder = RESET_PASSWORD_PLACEHOLDER_PASSWORD,
  redirect,
  requestId,
  showTile = true,
}) => {
  const apiUrl = isActivatePW ? URLACTIVATEPASSWORD : URLRESETPASSWORD;
  const toast = useToast();

  return (
    <div className="reset-password">
      <Tile conditional={showTile} title={RESET_PASSWORD_TITLE}>
        <TileBody conditional={showTile}>
          <Formik
            initialValues={{
              password: '',
              passwordconfirm: '',
            }}
            onSubmit={async (values) => {
              const response = await resetPassword(
                apiUrl,
                requestId,
                values.password,
              );
              if (response.status === (200 || 202)) {
                const expires = new Date();
                expires.setFullYear(expires.getFullYear() + 1);
                Cookies.save(COOKIES.hasPassword, 'true', {
                  domain: `.${process.env.NEXT_PUBLIC_DOMAIN_NAME}`,
                  expires,
                  path: '/',
                  samesite: 'none',
                });
                redirect();
              } else {
                toast.addToast(
                  response.data.message || RESET_PASSWORD_ERROR,
                  'toast-error',
                );
              }
            }}
            validationSchema={schema}
          >
            {({
              dirty,
              errors,
              isSubmitting,
              isValid,
              handleBlur,
              handleChange,
              setFieldTouched,
              touched,
              values,
            }) => (
              <Form>
                <WrappedInput
                  error={errors.password && touched.password}
                  errorText={errors.password}
                  formType={TYPE_UNDERLINED}
                  icon={faCheck}
                  normal={!errors.password}
                  showValidIconOnly
                  valid={!errors.password && touched.password}
                >
                  <input
                    className="form-control"
                    name="password"
                    onBlur={handleBlur}
                    onChange={(event) => {
                      setFieldTouched('password');
                      handleChange(event);
                    }}
                    placeholder={passwordPlaceholder}
                    type="password"
                    value={values.password}
                  />
                </WrappedInput>
                <WrappedInput
                  error={errors.passwordconfirm && touched.passwordconfirm}
                  errorText={errors.passwordconfirm}
                  formType={TYPE_UNDERLINED}
                  icon={faCheck}
                  normal={!errors.passwordconfirm}
                  showValidIconOnly
                  valid={!errors.passwordconfirm && touched.passwordconfirm}
                >
                  <input
                    className="form-control"
                    name="passwordconfirm"
                    onBlur={handleBlur}
                    onChange={(event) => {
                      setFieldTouched('passwordconfirm');
                      handleChange(event);
                    }}
                    placeholder={confirmPasswordPlaceholder}
                    type="password"
                    value={values.passwordconfirm}
                  />
                </WrappedInput>
                <ButtonForm
                  /* eslint-disable-next-line react/forbid-component-props */
                  className="full-width"
                  disabled={!(isValid && dirty) || isSubmitting}
                  type="submit"
                >
                  {buttonText}
                </ButtonForm>
              </Form>
            )}
          </Formik>
        </TileBody>
      </Tile>
    </div>
  );
};

ResetPasswordForm.propTypes = {
  buttonText: PropTypes.string,
  confirmPasswordPlaceholder: PropTypes.string,
  isActivatePW: PropTypes.bool,
  passwordPlaceholder: PropTypes.string,
  redirect: PropTypes.func,
  requestId: PropTypes.string,
  showTile: PropTypes.bool,
};

ResetPasswordForm.defaultProps = {
  requestId: '',
};

export default ResetPasswordForm;
